/**
 * Pre-built spreads.
 *
 * Flat: one bet type, one chip on each bet. Chosen to span the **coverage
 * curve** rather than to be good, because coverage is the only dial there is:
 *
 *     win ÷ loss = (36 − covered) ÷ covered
 *
 * Every payout satisfies `payout + 1 = 36 ÷ pockets-per-bet`, so the bet type
 * cancels out entirely. Twelve splits, eight streets, six corners, four six
 * lines and two dozens all cover 24 pockets and all pay 0.5 to 1. Bet type does
 * not change the return; it changes how finely the coverage can be placed
 * around the ring. That granularity is visible in this set — at 24 covered,
 * splits leave thirteen isolated bares with only three close pairs, while
 * corners cannot avoid a run of two.
 *
 * *Which* pockets each spread leaves bare was searched (`generate.ts`, a
 * build-time tool the running app never calls) against one objective: shortest
 * runs of adjacent bares first, then fewest pairs of bares sitting two apart.
 * Nothing else. The figures quoted on each entry are verified against the wheel
 * in `presets.test.ts` rather than trusted from these comments.
 *
 * A note on where arrangement is even a choice. Zero adjacent bares needs every
 * bare separated by at least two, so at most 18 of them — which means at least
 * 19 covered. Below that, runs are forced by counting alone and the search can
 * only make them shorter and fewer, not remove them.
 */

import type { PlacedBet, WheelStyle } from './types';

export interface SpreadPreset {
  readonly id: string;
  readonly name: string;
  /** What it is for, in the terms the player actually chooses between. */
  readonly description: string;
  readonly wheelStyle: WheelStyle;
  readonly bets: readonly PlacedBet[];
  /** Pockets covered, at one chip a bet. */
  readonly covered: number;
  /** Longest run of adjacent uncovered pockets on the ring. */
  readonly longestGap: number;
  /** How many runs are that long. */
  readonly runsAtLongest: number;
  /** Bares with exactly one covered pocket between them. */
  readonly nearPairs: number;
  /** Net chips if a covered pocket hits, and if none does. */
  readonly win: number;
  readonly loss: number;
}

const bets = (betType: PlacedBet['betType'], targets: readonly string[]): PlacedBet[] =>
  targets.map((target) => ({ betType, target, chips: 1 }));

export const SPREAD_PRESETS: readonly SpreadPreset[] = [
  {
    id: 'sp_2_streets',
    name: 'Two streets',
    description:
      'The far end of the curve: wins about one spin in six, and pays five times the stake when it does. Thirty-one pockets bare, in unavoidable runs.',
    wheelStyle: 'single-zero',
    bets: bets('street', ['22|23|24', '25|26|27']),
    covered: 6,
    longestGap: 7,
    runsAtLongest: 3,
    nearPairs: 6,
    win: 10,
    loss: 2,
  },
  {
    id: 'sp_column_3',
    name: 'One column',
    description:
      'A fixed block of twelve on the felt, which scatters on the ring wherever it happens to land. The best-arranged of the six columns and dozens; the arrangement is not a choice here, which is the point of including it.',
    wheelStyle: 'single-zero',
    bets: bets('column', ['3']),
    covered: 12,
    longestGap: 4,
    runsAtLongest: 3,
    nearPairs: 10,
    win: 2,
    loss: 1,
  },
  {
    id: 'sp_3_corners',
    name: 'Three corners',
    description:
      'The same twelve pockets as a column, but placed rather than given — three corners can be put where they scatter best, and the worst run drops from four to three.',
    wheelStyle: 'single-zero',
    bets: bets('corner', ['11|12|14|15', '17|18|20|21', '23|24|26|27']),
    covered: 12,
    longestGap: 3,
    runsAtLongest: 3,
    nearPairs: 10,
    win: 6,
    loss: 3,
  },
  {
    id: 'sp_9_splits',
    name: 'Nine splits',
    description:
      'Break-even ground: wins as often as it loses, and pays what it stakes. Nineteen bares is one too many to keep them all apart, so two runs of two are forced.',
    wheelStyle: 'single-zero',
    bets: bets('split', [
      '0|3', '2|5', '4|7', '12|15', '17|20', '22|23', '27|30', '31|34', '33|36',
    ]),
    covered: 18,
    longestGap: 2,
    runsAtLongest: 2,
    nearPairs: 16,
    win: 9,
    loss: 9,
  },
  {
    id: 'sp_3_six_lines',
    name: 'Three six lines',
    description:
      'The same even-money shape in the coarsest unit available. Only one run of two, against nine splits’ two — but eighteen close pairs, because six-pocket blocks cannot be threaded as finely.',
    wheelStyle: 'single-zero',
    bets: bets('six-line', [
      '19|20|21|22|23|24', '25|26|27|28|29|30', '31|32|33|34|35|36',
    ]),
    covered: 18,
    longestGap: 2,
    runsAtLongest: 1,
    nearPairs: 18,
    win: 3,
    loss: 3,
  },
  {
    id: 'sp_12_splits',
    name: 'Twelve splits',
    description:
      'Twenty-four covered at the finest granularity on the felt, and it shows: thirteen bares, every one isolated, only three of them sitting within a pocket of another. The best-arranged spread in this set.',
    wheelStyle: 'single-zero',
    bets: bets('split', [
      '2|3', '5|8', '6|9', '7|10', '11|14', '12|15',
      '16|17', '19|22', '20|21', '26|27', '29|32', '33|36',
    ]),
    covered: 24,
    longestGap: 1,
    runsAtLongest: 13,
    nearPairs: 3,
    win: 6,
    loss: 12,
  },
  {
    id: 'sp_8_streets',
    name: 'Eight streets',
    description:
      'Twenty-four covered in threes. Every bare isolated, six of them close-paired — the middle of the granularity range, and a lighter stake than the splits for the same return.',
    wheelStyle: 'single-zero',
    bets: bets('street', [
      '1|2|3', '4|5|6', '7|8|9', '10|11|12',
      '13|14|15', '16|17|18', '25|26|27', '31|32|33',
    ]),
    covered: 24,
    longestGap: 1,
    runsAtLongest: 13,
    nearPairs: 6,
    win: 4,
    loss: 8,
  },
  {
    id: 'sp_6_corners',
    name: 'Six corners',
    description:
      'Twenty-four covered in 2×2 blocks. The only spread at this coverage that cannot avoid a run of two — corners on a three-row felt have too little freedom. Six chips to win three.',
    wheelStyle: 'single-zero',
    bets: bets('corner', [
      '2|3|5|6', '8|9|11|12', '13|14|16|17',
      '19|20|22|23', '25|26|28|29', '32|33|35|36',
    ]),
    covered: 24,
    longestGap: 2,
    runsAtLongest: 1,
    nearPairs: 5,
    win: 3,
    loss: 6,
  },
  {
    id: 'sp_4_six_lines',
    name: 'Four six lines',
    description:
      'Twenty-four covered in the fewest bets possible for it. Runs stay at one, but seven close pairs — coarse blocks cluster the bares more than fine ones do.',
    wheelStyle: 'single-zero',
    bets: bets('six-line', [
      '7|8|9|10|11|12', '16|17|18|19|20|21',
      '22|23|24|25|26|27', '31|32|33|34|35|36',
    ]),
    covered: 24,
    longestGap: 1,
    runsAtLongest: 13,
    nearPairs: 7,
    win: 2,
    loss: 4,
  },
  {
    id: 'sp_2_dozens',
    name: 'Two dozens',
    description:
      'Twenty-four covered with two chips — the cheapest way to reach this coverage, and the least arrangeable. The best of the twelve possible column and dozen pairs, which is a choice between three options rather than a search.',
    wheelStyle: 'single-zero',
    bets: bets('dozen', ['2', '3']),
    covered: 24,
    longestGap: 2,
    runsAtLongest: 1,
    nearPairs: 6,
    win: 1,
    loss: 2,
  },
  {
    id: 'sp_10_streets',
    name: 'Ten streets',
    description:
      'The near end of the curve. Seven bares, every one isolated and none within a pocket of another — the only spread here with no close pairs at all. Ten chips at risk to win two.',
    wheelStyle: 'single-zero',
    bets: bets('street', [
      '0|1|2', '10|11|12', '13|14|15', '16|17|18', '19|20|21',
      '22|23|24', '25|26|27', '28|29|30', '31|32|33', '34|35|36',
    ]),
    covered: 30,
    longestGap: 1,
    runsAtLongest: 7,
    nearPairs: 0,
    win: 2,
    loss: 10,
  },
];

export function presetsFor(style: WheelStyle): SpreadPreset[] {
  return SPREAD_PRESETS.filter((preset) => preset.wheelStyle === style);
}

export function presetById(id: string): SpreadPreset | undefined {
  return SPREAD_PRESETS.find((preset) => preset.id === id);
}

/** Scale a preset to a chip unit, keeping whole chips. */
export function presetBets(preset: SpreadPreset, unit = 1): PlacedBet[] {
  return preset.bets.map((bet) => ({ ...bet, chips: bet.chips * unit }));
}
